/** The grip on the sidebar's edge: drag to widen, arrow keys to nudge. */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  SIDEBAR_MAX_WIDTH,
  SIDEBAR_MIN_WIDTH,
  SIDEBAR_WIDTH_STEP,
} from "../state/useSidebarWidth.ts";

interface SidebarResizerProps {
  readonly width: number;
  readonly onResize: (width: number) => void;
  readonly onReset: () => void;
  readonly onDraggingChange: (dragging: boolean) => void;
}

export function SidebarResizer({
  width,
  onResize,
  onReset,
  onDraggingChange,
}: SidebarResizerProps) {
  const [dragging, setDragging] = useState(false);

  /**
   * The drag reads from a ref rather than from props: `pointermove` handlers are
   * bound once per drag, and a closed-over `width` would freeze at whatever it
   * was when the pointer went down.
   */
  const origin = useRef({ x: 0, width });

  const beginDrag = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      // Left button only — a right-click here should open the context menu.
      if (event.button !== 0) return;
      event.preventDefault();

      origin.current = { x: event.clientX, width };
      setDragging(true);
      onDraggingChange(true);
    },
    [width, onDraggingChange],
  );

  useEffect(() => {
    if (!dragging) return;

    const onMove = (event: PointerEvent) => {
      onResize(origin.current.width + (event.clientX - origin.current.x));
    };

    const stop = () => {
      setDragging(false);
      onDraggingChange(false);
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", stop);
    // A cancelled pointer (gesture stolen, window blurred) must not leave the
    // app stuck in a drag that follows the cursor forever.
    window.addEventListener("pointercancel", stop);

    /**
     * While the grip is held the cursor belongs to the drag, not to whatever it
     * happens to pass over, and text under it must not select.
     */
    const { cursor, userSelect } = document.body.style;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", stop);
      window.removeEventListener("pointercancel", stop);
      document.body.style.cursor = cursor;
      document.body.style.userSelect = userSelect;
    };
  }, [dragging, onResize, onDraggingChange]);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      const step = event.shiftKey ? SIDEBAR_WIDTH_STEP * 4 : SIDEBAR_WIDTH_STEP;

      switch (event.key) {
        case "ArrowLeft":
          onResize(width - step);
          break;
        case "ArrowRight":
          onResize(width + step);
          break;
        case "Home":
          onResize(SIDEBAR_MIN_WIDTH);
          break;
        case "End":
          onResize(SIDEBAR_MAX_WIDTH);
          break;
        // The keyboard equivalent of the double-click reset.
        case "Enter":
        case " ":
          onReset();
          break;
        default:
          return;
      }

      event.preventDefault();
    },
    [width, onResize, onReset],
  );

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize sidebar"
      aria-valuenow={width}
      aria-valuemin={SIDEBAR_MIN_WIDTH}
      aria-valuemax={SIDEBAR_MAX_WIDTH}
      tabIndex={0}
      data-dragging={dragging}
      onPointerDown={beginDrag}
      onDoubleClick={onReset}
      onKeyDown={onKeyDown}
      title="Drag to resize — double-click to reset"
      /* There is no seam below `md` — the sidebar is an overlay there and owns
         no track, so a grip on its edge would sit in the middle of the page. */
      className="group absolute inset-y-0 left-[var(--sidebar-width)] z-20 hidden w-2 -translate-x-1/2 cursor-col-resize touch-none outline-none md:block"
    >
      {/* The hit area is 8px wide; only this hairline is ever painted. */}
      <div className="mx-auto h-full w-px bg-transparent transition-colors group-hover:bg-border-strong group-focus-visible:bg-ring group-data-[dragging=true]:bg-ring" />
    </div>
  );
}
